import { useMemo } from 'react'

import { differenceInSeconds, formatDuration, intervalToDuration, parseISO } from 'date-fns'
import { AlertTriangle, ChartArea, Check, Info, List } from 'lucide-react'

import { cn } from '@fortem-monorepo/js-utilities'
import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { groupByKey } from '@fortem-monorepo/js-utilities'
import { ViewContainer } from '@fortem-monorepo/react-spa'
import { Button } from '@fortem-monorepo/react-ui'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@fortem-monorepo/react-ui'
import { Skeleton } from '@fortem-monorepo/react-ui'
import { ToggleGroup, ToggleGroupItem } from '@fortem-monorepo/react-ui'
import { Logs } from '@fortem-monorepo/web-sdk'

import { ToggleGroupProvider } from '@/hooks/providers/ToggleGroupProvider'
import { DailyTasksChart } from '@/pages/monitoring/_dashboard/components/DailyTasksChart'
import { useMonitoringContext } from '@/pages/monitoring/_dashboard/components/MonitoringContext'
import { TaskGroupTab } from '@/pages/monitoring/_dashboard/components/TaskGroupTab'

const SummaryTab: React.FC = () => {
  const {
    scheduledTasks,
    completedTasks,
    failedTasks,
    selectedDate,
    addWindowTab,
    visibleTasks,
    scheduledTasksQuery,
  } = useMonitoringContext()

  const completedGroupedByTaskName = useMemo(
    () => groupByKey(completedTasks, 'description'),
    [completedTasks],
  )
  const failedGroupedByTaskName = useMemo(
    () => groupByKey(failedTasks, 'description'),
    [failedTasks],
  )

  // Augments all visible tasks with duration information
  const tasksWithDurations = useMemo(() => {
    return visibleTasks
      .map((task) => {
        const _new: Logs.ScheduledTask & { duration?: number } = { ...task }

        if (task.update_datetime_utc && task.status === 2) {
          const uploadDate = parseISO(task.upload_datetime_utc)
          const completedDate = parseISO(task.update_datetime_utc)
          const duration = differenceInSeconds(completedDate, uploadDate)
          _new['duration'] = duration
        }

        return _new
      })
      .filter((task) => task.duration)
      .sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0))
  }, [scheduledTasks, selectedDate])

  // The longest visible task
  const longestTask = useMemo(() => tasksWithDurations?.[0], [tasksWithDurations])

  return (
    <ViewContainer className={'@container flex h-full'}>
      <div className={'grid grid-cols-1 @lg:grid-cols-3 gap-4 w-full'}>
        {scheduledTasksQuery.isLoading ? (
          <Skeleton className={'w-full h-full'} />
        ) : (
          <Card
            variant={'simple'}
            className={cn(['flex flex-col h-full shadow-none overflow-hidden'])}
          >
            <ToggleGroupProvider initialValue="list">
              {({ value, setValue }) => {
                return (
                  <>
                    <CardHeader>
                      <CardDescription>Completed</CardDescription>
                      <CardTitle className={'flex items-center gap-2'}>
                        <Check size={18} className={'text-green-600'} />
                        {completedTasks.length}
                      </CardTitle>
                      <CardAction>
                        <ToggleGroup
                          className={'bg-white border-none!'}
                          type="single"
                          value={value}
                          onValueChange={setValue}
                          variant={'outline'}
                          size={'sm'}
                        >
                          <ToggleGroupItem
                            value="list"
                            className={
                              'data-[state=on]:bg-green-100 border-green-600/20 cursor-pointer'
                            }
                          >
                            <List />
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            value="chart"
                            className={
                              'data-[state=on]:bg-green-100 border-green-600/20 cursor-pointer '
                            }
                          >
                            <ChartArea />
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </CardAction>
                    </CardHeader>
                    <CardContent className={'h-full'}>
                      {value === 'list' && (
                        <ul className={'space-y-0'}>
                          {Object.entries(completedGroupedByTaskName)
                            .sort((a, b) => b[1].length - a[1].length)
                            .map(([taskName, tasks]) => (
                              <li key={taskName} className={''}>
                                <Button
                                  size="sm"
                                  variant="link"
                                  className={'w-full justify-between text-xs'}
                                  onClick={() => {
                                    const title = `Completed | ${taskName} | ${toISO8601(selectedDate)}`
                                    const id = `completed-${taskName}-${toISO8601(selectedDate)}`
                                    addWindowTab({
                                      title,
                                      id,
                                      component: (
                                        <TaskGroupTab
                                          date={selectedDate}
                                          taskName={taskName}
                                          status={2}
                                          key={id}
                                        />
                                      ),
                                    })
                                  }}
                                >
                                  <span
                                    className={
                                      'text-primary whitespace-nowrap text-ellipsis overflow-hidden'
                                    }
                                  >
                                    {taskName}
                                  </span>
                                  <span>{tasks.length}</span>
                                </Button>
                              </li>
                            ))}
                        </ul>
                      )}
                      {value === 'chart' && (
                        <DailyTasksChart tasks={visibleTasks} date={selectedDate} />
                      )}
                    </CardContent>
                  </>
                )
              }}
            </ToggleGroupProvider>
          </Card>
        )}
        {scheduledTasksQuery.isLoading ? (
          <Skeleton className={'w-full h-full'} />
        ) : (
          <Card
            variant="simple"
            className={cn(['flex flex-col h-full shadow-none overflow-hidden'])}
          >
            <CardHeader>
              <CardDescription>Failed</CardDescription>
              <CardTitle className={'flex items-center gap-2'}>
                <AlertTriangle size={18} className={'text-red-600'} />
                {failedTasks.length}
              </CardTitle>
            </CardHeader>
            <CardContent className={'h-full'}>
              <ul className={'space-y-1'}>
                {Object.entries(failedGroupedByTaskName)
                  .sort((a, b) => b[1].length - a[1].length)
                  .map(([taskName, tasks]) => (
                    <li key={taskName}>
                      <Button
                        size="sm"
                        variant="link"
                        className={'w-full justify-between text-xs'}
                        onClick={() => {
                          const title = `Failed | ${taskName} | ${toISO8601(selectedDate)}`
                          const id = `failed-${taskName}-${toISO8601(selectedDate)}`
                          addWindowTab({
                            title,
                            id,
                            component: (
                              <TaskGroupTab
                                date={selectedDate}
                                taskName={taskName}
                                status={1}
                                key={id}
                              />
                            ),
                          })
                        }}
                      >
                        <span
                          className={'text-primary whitespace-nowrap text-ellipsis overflow-hidden'}
                        >
                          {taskName}
                        </span>
                        <span>{tasks.length}</span>
                      </Button>
                    </li>
                  ))}
              </ul>
            </CardContent>
          </Card>
        )}
        {!scheduledTasksQuery.isLoading ? (
          longestTask && (
            <Card
              variant="simple"
              className={cn(['flex flex-col h-full shadow-none overflow-hidden'])}
            >
              <CardHeader>
                <CardDescription>Longest</CardDescription>
                <CardTitle className={'flex items-center gap-2'}>
                  <Info size={18} className={'text-blue-600'} />
                  {longestTask.description}
                </CardTitle>
              </CardHeader>
              <CardContent className={'h-full'}>
                <ul className={'space-y-1'}>
                  {tasksWithDurations.slice(0, 20)?.map((task) => {
                    return (
                      <li key={task.id} className={''}>
                        <Button
                          size="sm"
                          variant="link"
                          className={'w-full justify-between text-xs'}
                          onClick={() => {
                            const title = `Completed | ${task.description} | ${toISO8601(selectedDate)}`
                            const id = `completed-${task.description}-${toISO8601(selectedDate)}`
                            addWindowTab({
                              title,
                              id,
                              component: (
                                <TaskGroupTab
                                  date={selectedDate}
                                  taskName={task.description}
                                  status={2}
                                  key={id}
                                />
                              ),
                            })
                          }}
                        >
                          <span
                            className={
                              'text-primary whitespace-nowrap text-ellipsis overflow-hidden'
                            }
                          >
                            {task.description}
                          </span>
                          <span>
                            {formatDuration(
                              intervalToDuration({
                                start: new Date(task.upload_datetime_utc),
                                end: new Date(task.update_datetime_utc),
                              }),
                            )
                              .replace('minutes', 'mins')
                              .replace('minute', 'min')
                              .replace('seconds', 'secs')
                              .replace('second', 'sec')}
                          </span>
                        </Button>
                      </li>
                    )
                  })}
                </ul>
              </CardContent>
            </Card>
          )
        ) : (
          <Skeleton className={'w-full h-full'} />
        )}
      </div>
    </ViewContainer>
  )
}

export default SummaryTab
